/**
 * Shared display formatters for the TrEND dashboard.
 */

const NA = "NA";

export function fmtInt(value: any): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return NA;
    }
    return Math.round(Number(value)).toLocaleString();
}

export function fmtFloat(value: any, digits = 1): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return NA;
    }
    const n = Number(value);
    // Keep decimals only for small (<1) non-zero values; round everything else to a whole number.
    if (n !== 0 && Math.abs(n) < 1) {
        return n.toLocaleString(undefined, {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        });
    }
    return Math.round(n).toLocaleString();
}

export function fmtPct(value: any, digits = 1): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return NA;
    }
    return (Number(value) * 100).toFixed(digits) + "%";
}

export function fmtDate(value: any): string {
    if (!value) return NA;
    const d = typeof value === "string" ? value.slice(0, 10) : String(value);
    return d;
}

/**
 * Values with their unit attached.
 */
export const fmtHours = (value: any): string => `${fmtFloat(value, 1)} hrs`;
export const fmtKm = (value: any): string => `${fmtFloat(value, 0)} km`;
export const fmtTonnes = (value: any): string => `${fmtFloat(value, 1)} t`;
export const fmtKmDays = (value: any): string =>
    `${fmtFloat(value, 2)} km-days`;

/**
 * The same units again, as plain text to paste onto the end of a chart hover.
 *
 * Charts can't use the functions above: Plotly builds its own hover text from a template
 * and formats the number itself, so all we can add is the unit that follows it. The
 * leading space is part of the string.
 */
export const UNIT_SUFFIX = {
    hours: " hrs",
    km: " km",
    kmDays: " km-days",
    tonnes: " t",
    none: "",
};

/**
 * Which unit each metric is shown in, so the Overview KPI cards and the Daily Trend chart
 * display the same metric the same way. Anything not listed here is shown as a plain
 * whole number, which is right for the counts.
 */
export const METRIC_VALUE_FORMATS: Record<string, (v: any) => string> = {
    road_delta_time_hours: fmtHours,
    road_delta_length_km: fmtKm,
    impacted_tonnes: fmtTonnes,
    rerouted_tonnes: fmtTonnes,
    blocked_tonnes: fmtTonnes,
};

/** How to display one metric's value. Accepts either the plain column name or the
 * `_sum` version the range totals use. Anything unlisted shows as a whole number. */
export function formatForMetric(metric: string): (v: any) => string {
    const base = metric.endsWith("_sum")
        ? metric.slice(0, -"_sum".length)
        : metric;
    return METRIC_VALUE_FORMATS[base] ?? fmtInt;
}
